import { useUserData } from '@/composables/useUserData';

export default defineNuxtPlugin(async () => {
  // Get the current user session
  const { session, fetch, clear } = useUserSession();
  const { clearUserData, fetchUserDetails } = useUserData();
  const { $localeRootPath } = useNuxtApp();
  const router = useRouter();
  const route = useRoute();

  // Function to handle logout
  const handleLogout = async () => {
    // Clear user data
    clearUserData();

    // Clear session
    await clear();

    // only redirect if the user is on account pages
    if (route.path.includes('/account/')) {
      // Store the current path for redirect after login
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fromUri', window.location.origin + router.currentRoute.value.fullPath);
      }

      window.location.replace($localeRootPath('/account/login'));
    }
  };

  // If we have a session but it's approaching expiration, refresh it
  if (session.value?.idToken) {
    try {
      await fetchUserDetails();
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    }

    const tokenData = decodeToken(session.value.idToken);

    if (tokenData && isTokenExpiringSoon(tokenData.exp)) {
      try {
        // Call our refresh endpoint
        await useFetch('/api/auth/refresh', { method: 'POST' });
        // Update the session
        await fetch();
      } catch (error) {
        console.error('Failed to refresh token:', error);

        // If refresh fails, log the user out and optionally redirect to login page
        await handleLogout();
      }
    }

    // Check if token is already expired
    if (tokenData && isTokenExpired(tokenData.exp)) {
      console.warn('Token has expired. Redirecting to login page...');
      await handleLogout();
    }
  }
});

// Helper function to decode JWT token
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

// Check if token expires in less than 5 minutes
function isTokenExpiringSoon(exp) {
  if (!exp) return true;

  const now = Math.floor(Date.now() / 1000);
  const fiveMinutesInSeconds = 5 * 60;

  return exp - now < fiveMinutesInSeconds;
}

// Check if token is already expired
function isTokenExpired(exp) {
  if (!exp) return true;

  const now = Math.floor(Date.now() / 1000);

  return now >= exp;
}
